import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'

import { DefaultLoadingComponent } from './DefaultLoadingComponent'
import { DefaultUnauthorizedComponent } from './DefaultUnauthorizedComponent'

export type ProtectedRouteProps = {
  /**
   * Permisos requeridos para acceder a la ruta
   * Si no se especifica, solo requiere autenticación
   */
  requiredRoles?: string[]

  /**
   * Ruta a la que redirigir si no está autenticado
   * @default '/login'
   */
  redirectTo?: string

  /**
   * Componente a mostrar mientras se verifica la autenticación
   */
  loadingComponent?: React.ReactNode

  /**
   * Componente a mostrar si no tiene permisos
   */
  unauthorizedComponent?: React.ReactNode
}

/**
 * Componente que protege rutas requiriendo autenticación y opcionalmente permisos específicos
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRoles = [],
  redirectTo = '/login',
  loadingComponent = <DefaultLoadingComponent />,
  unauthorizedComponent = <DefaultUnauthorizedComponent />
}) => {
  const location = useLocation()
  const { isLoading, isAuthenticated, hasRoles, isAdmin } = useAuth()

  // Mientras verifica autenticación, mostrar loading
  if (isLoading) {
    return <>{loadingComponent}</>
  }

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    // Guardar la ruta actual para redirigir después del login
    return (
      <Navigate to={redirectTo} state={{ from: location.pathname + location.search }} replace />
    )
  }

  // Si requiere permisos específicos y no los tiene
  console.warn(`ProtectedRoute: Usuario autenticado con roles: ${requiredRoles.join(', ')}`)
  if (requiredRoles.length > 0 && !hasRoles(requiredRoles)) {
    // Si es admin pero intenta acceder a una ruta que no tiene permisos
    // podríamos redirigir al dashboard en lugar de mostrar unauthorized
    if (isAdmin()) {
      return <Navigate to="/dashboard" replace />
    }

    return <>{unauthorizedComponent}</>
  }

  // Usuario autenticado y con permisos, renderizar children
  return <Outlet />
}
